import React, { useState } from 'react';
import { Building2, ChevronDown, Check, Search } from 'lucide-react';
import { useEmpresa } from '../contexts/EmpresaContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';

const EmpresaSelector = () => {
  const {
    empresaAtual,
    empresasDisponiveis,
    trocarEmpresa,
    loading,
    isUsuarioEscritorio,
    totalEmpresas
  } = useEmpresa();

  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  // Se não é usuário de escritório ou só tem uma empresa, não mostrar selector
  if (!isUsuarioEscritorio() || totalEmpresas <= 1) {
    return null;
  }

  // Filtrar empresas pela busca
  const empresasFiltradas = empresasDisponiveis.filter(empresa => {
    const termo = searchTerm.toLowerCase();
    return (
      empresa.razao_social?.toLowerCase().includes(termo) ||
      empresa.nome_fantasia?.toLowerCase().includes(termo) ||
      empresa.cnpj?.includes(termo)
    );
  });

  const handleSelect = async (empresaId) => {
    if (empresaId !== empresaAtual?.id) {
      await trocarEmpresa(empresaId);
    }
    setIsOpen(false);
    setSearchTerm('');
  };

  const formatCNPJ = (cnpj) => {
    if (!cnpj) return '';
    // Remove caracteres não numéricos
    const numeros = cnpj.replace(/\D/g, '');
    // Formata como XX.XXX.XXX/XXXX-XX
    return numeros.replace(
      /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
      '$1.$2.$3/$4-$5'
    );
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="flex items-center gap-2 min-w-[200px] max-w-[300px] justify-between bg-slate-800 border-slate-600 hover:bg-slate-700 text-white"
          disabled={loading}
          data-testid="empresa-selector-trigger"
        >
          <div className="flex items-center gap-2 truncate">
            <Building2 className="h-4 w-4 text-blue-400 flex-shrink-0" />
            <span className="truncate">
              {empresaAtual?.nome_fantasia || empresaAtual?.razao_social || 'Selecione uma empresa'}
            </span>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <Badge variant="secondary" className="text-xs bg-slate-600">
              {totalEmpresas}
            </Badge>
            <ChevronDown className="h-4 w-4" />
          </div>
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent 
        className="w-[350px] bg-slate-800 border-slate-600"
        align="start"
      >
        <DropdownMenuLabel className="text-slate-300">
          Trocar Empresa
        </DropdownMenuLabel>
        
        <div className="p-2">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Buscar por nome ou CNPJ..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
              data-testid="empresa-selector-search"
            />
          </div>
        </div>
        
        <DropdownMenuSeparator className="bg-slate-600" />
        
        <div className="max-h-[300px] overflow-y-auto">
          {empresasFiltradas.length === 0 ? (
            <div className="p-4 text-center text-slate-400 text-sm">
              Nenhuma empresa encontrada
            </div>
          ) : (
            empresasFiltradas.map((empresa) => (
              <DropdownMenuItem
                key={empresa.id}
                onClick={() => handleSelect(empresa.id)}
                className={`
                  cursor-pointer p-3 focus:bg-slate-700
                  ${empresa.id === empresaAtual?.id ? 'bg-slate-700' : ''}
                `}
                data-testid={`empresa-option-${empresa.id}`}
              >
                <div className="flex items-start justify-between w-full gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-white truncate">
                      {empresa.nome_fantasia || empresa.razao_social}
                    </div>
                    <div className="text-xs text-slate-400 truncate">
                      {formatCNPJ(empresa.cnpj)}
                    </div>
                    {empresa.nome_fantasia && empresa.razao_social !== empresa.nome_fantasia && (
                      <div className="text-xs text-slate-500 truncate">
                        {empresa.razao_social}
                      </div>
                    )}
                  </div>
                  {empresa.id === empresaAtual?.id && (
                    <Check className="h-4 w-4 text-green-400 flex-shrink-0 mt-1" />
                  )}
                </div>
              </DropdownMenuItem>
            ))
          )}
        </div>
        
        <DropdownMenuSeparator className="bg-slate-600" />
        
        <div className="p-2 text-xs text-slate-400 text-center">
          {totalEmpresas} empresa{totalEmpresas !== 1 ? 's' : ''} disponíve{totalEmpresas !== 1 ? 'is' : 'l'}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default EmpresaSelector;
